import React, { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import api from "../lib/api";
import { useAuth } from "../lib/authContext";
import { Layers, ArrowRightLeft, XCircle, RefreshCw } from "lucide-react";
import CancellationReasons from "../components/orders/CancellationReasons";
import TransferReasons from "../components/orders/TransferReasons";

const ORDER_STATUSES = [
  "Dispatched",
  "Delivered",
  "RTO",
  "InvoiceGenerated",
];

const BulkActions: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();

  // Bulk Transfer state
  const [transferInvoiceNumbers, setTransferInvoiceNumbers] = useState("");
  const [selectedPharmacy, setSelectedPharmacy] = useState("0");
  const [transferReason, setTransferReason] = useState("");
  const [eligibilityComments, setEligibilityComments] = useState<string[]>([]);
  const [isEligible, setIsEligible] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);
  const [entities, setEntities] = useState<{ value: number; label: string }[]>(
    [],
  );

  // Bulk Cancel state
  const [cancelInvoiceNumbers, setCancelInvoiceNumbers] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);

  // Bulk Update state
  const [updateInvoiceNumbers, setUpdateInvoiceNumbers] = useState("");
  const [updateStatus, setUpdateStatus] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    fetchEntities();
  }, []);

  const fetchEntities = async () => {
    try {
      const response = await api.getAllEntities("");
      if (response?.results) {
        const formatted = response.results.map((entity: any) => ({
          value: Number(entity.id),
          label: `${entity.name} (${entity.branch_id})`,
        }));
        setEntities([{ value: 0, label: "Select a pharmacy" }, ...formatted]);
      }
    } catch (error) {
      console.error("Failed to fetch entities:", error);
    }
  };

  // --- Bulk Transfer ---
  const checkEligibility = async () => {
    setIsChecking(true);
    setIsEligible(false);
    try {
      const response = await api.checkBulkTransferEligibility({
        invoice_numbers: transferInvoiceNumbers
          .split(",")
          .map((n) => n.trim())
          .filter((n) => n),
        pharmacy_id: Number(selectedPharmacy),
      });

      if (
        response.success &&
        (!response.comments || response.comments.length === 0)
      ) {
        setIsEligible(true);
        setEligibilityComments([]);
      } else {
        setIsEligible(false);
        setEligibilityComments(response.comments || []);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to check eligibility",
        variant: "destructive",
      });
    } finally {
      setIsChecking(false);
    }
  };

  const handleBulkTransfer = async () => {
    if (!transferReason.trim()) {
      toast({
        title: "Transfer Reason Required",
        description: "Please provide a transfer reason.",
        variant: "destructive",
      });
      return;
    }

    setIsTransferring(true);
    try {
      const response = await api.bulkTransferOrders({
        invoice_numbers: transferInvoiceNumbers
          .split(",")
          .map((n) => n.trim())
          .filter((n) => n),
        pharmacy_id: Number(selectedPharmacy),
        user_id: user?.id ?? "",
        transfer_reason: transferReason,
      });

      if (response.success) {
        toast({
          title: "Success",
          description: "Bulk transfer initiated successfully",
        });
        setTransferInvoiceNumbers("");
        setSelectedPharmacy("0");
        setTransferReason("");
        setIsEligible(false);
        setEligibilityComments([]);
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to initiate bulk transfer",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to initiate bulk transfer",
        variant: "destructive",
      });
    } finally {
      setIsTransferring(false);
    }
  };

  // --- Bulk Cancel ---
  const handleBulkCancel = async () => {
    if (!cancelInvoiceNumbers || !cancelReason) {
      toast({
        title: "Missing information",
        description: "Please provide invoice numbers and a cancellation reason",
        variant: "destructive",
      });
      return;
    }

    if (!window.confirm("Are you sure you want to cancel these orders?")) {
      return;
    }

    setIsCancelling(true);
    try {
      const response = await api.bulkCancelOrders({
        invoice_numbers: cancelInvoiceNumbers
          .split(",")
          .map((n) => n.trim())
          .filter((n) => n),
        reject_reason: cancelReason,
        user_email: user?.email || "",
        user_id: user?.id || "",
      });

      if (response.success) {
        toast({
          title: "Success",
          description:
            response.message || "Bulk cancellation completed successfully",
        });
        setCancelInvoiceNumbers("");
        setCancelReason("");
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to cancel orders",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to cancel orders",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  // --- Bulk Update Status ---
  const handleBulkUpdate = async () => {
    if (!updateInvoiceNumbers || !updateStatus) {
      toast({
        title: "Missing information",
        description: "Please provide invoice numbers and select a status",
        variant: "destructive",
      });
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to update these orders to "${updateStatus}"?`,
      )
    ) {
      return;
    }

    setIsUpdating(true);
    try {
      const response = await api.bulkUpdateOrderStatus({
        invoice_numbers: updateInvoiceNumbers
          .split(",")
          .map((n) => n.trim())
          .filter((n) => n),
        status: updateStatus,
        user_id: user?.id || "",
      });

      if (response.success) {
        toast({
          title: "Success",
          description:
            response.message || "Bulk status update completed successfully",
        });
        setUpdateInvoiceNumbers("");
        setUpdateStatus("");
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to update order statuses",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update order statuses",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="p-6 min-h-screen bg-white dark:bg-gray-900">
      <div className="mb-8">
        <h1 className="text-2xl font-bold dark:text-white flex items-center gap-2">
          <Layers className="w-6 h-6" />
          Bulk Actions
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Perform bulk operations on multiple orders at once using
          comma-separated invoice numbers.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bulk Transfer Card */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-800 rounded-lg">
                <ArrowRightLeft className="w-5 h-5 text-blue-600 dark:text-blue-300" />
              </div>
              <div>
                <h2 className="text-lg font-semibold dark:text-white">
                  Bulk Transfer
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Transfer orders to another pharmacy
                </p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Invoice Numbers <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="Enter invoice numbers separated by commas"
                value={transferInvoiceNumbers}
                onChange={(e) => {
                  setTransferInvoiceNumbers(e.target.value);
                  setIsEligible(false);
                }}
                className="dark:bg-gray-700 dark:text-white dark:border-gray-600"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Target Pharmacy <span className="text-red-500">*</span>
              </label>
              <Select
                value={selectedPharmacy}
                onValueChange={(value) => {
                  setSelectedPharmacy(value);
                  setIsEligible(false);
                }}
              >
                <SelectTrigger className="dark:bg-gray-700 dark:text-white dark:border-gray-600">
                  <SelectValue placeholder="Select a pharmacy" />
                </SelectTrigger>
                <SelectContent className="dark:bg-gray-800 dark:text-white dark:border-gray-700">
                  {entities.map((entity) => (
                    <SelectItem
                      key={entity.value}
                      value={entity.value.toString()}
                    >
                      {entity.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Transfer Reason <span className="text-red-500">*</span>
              </label>
              <TransferReasons
                value={transferReason}
                onChange={setTransferReason}
              />
            </div>

            {eligibilityComments.length > 0 && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">
                  Validation Errors
                </p>
                <ul className="list-disc pl-4 space-y-1">
                  {eligibilityComments.map((comment) => (
                    <li
                      key={comment}
                      className="text-sm text-red-500 dark:text-red-400"
                    >
                      {comment}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end space-x-2 pt-2">
              {!isEligible ? (
                <Button
                  onClick={checkEligibility}
                  disabled={
                    !transferInvoiceNumbers ||
                    !selectedPharmacy ||
                    selectedPharmacy === "0" ||
                    isChecking
                  }
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isChecking ? "Checking..." : "Check Eligibility"}
                </Button>
              ) : (
                <Button
                  onClick={handleBulkTransfer}
                  disabled={!isEligible || isTransferring}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {isTransferring ? "Transferring..." : "Transfer Orders"}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Bulk Cancel Card */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/30 dark:to-orange-900/30">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 dark:bg-red-800 rounded-lg">
                <XCircle className="w-5 h-5 text-red-600 dark:text-red-300" />
              </div>
              <div>
                <h2 className="text-lg font-semibold dark:text-white">
                  Bulk Cancel
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Cancel multiple orders at once
                </p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Invoice Numbers <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="Enter invoice numbers separated by commas"
                value={cancelInvoiceNumbers}
                onChange={(e) => setCancelInvoiceNumbers(e.target.value)}
                className="dark:bg-gray-700 dark:text-white dark:border-gray-600"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Cancellation Reason <span className="text-red-500">*</span>
              </label>
              <CancellationReasons
                value={cancelReason}
                onChange={setCancelReason}
              />
            </div>

            <div className="flex justify-end space-x-2 pt-2">
              <Button
                variant="destructive"
                onClick={handleBulkCancel}
                disabled={
                  !cancelInvoiceNumbers || !cancelReason || isCancelling
                }
              >
                {isCancelling ? "Cancelling..." : "Cancel Orders"}
              </Button>
            </div>
          </div>
        </div>

        {/* Bulk Update Status Card */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-800 rounded-lg">
                <RefreshCw className="w-5 h-5 text-emerald-600 dark:text-emerald-300" />
              </div>
              <div>
                <h2 className="text-lg font-semibold dark:text-white">
                  Bulk Update Status
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Update status of multiple orders
                </p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Invoice Numbers <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="Enter invoice numbers separated by commas"
                value={updateInvoiceNumbers}
                onChange={(e) => setUpdateInvoiceNumbers(e.target.value)}
                className="dark:bg-gray-700 dark:text-white dark:border-gray-600"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                New Status <span className="text-red-500">*</span>
              </label>
              <Select value={updateStatus} onValueChange={setUpdateStatus}>
                <SelectTrigger className="dark:bg-gray-700 dark:text-white dark:border-gray-600">
                  <SelectValue placeholder="Select a status" />
                </SelectTrigger>
                <SelectContent className="dark:bg-gray-800 dark:text-white dark:border-gray-700">
                  {ORDER_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end space-x-2 pt-2">
              <Button
                onClick={handleBulkUpdate}
                disabled={
                  !updateInvoiceNumbers || !updateStatus || isUpdating
                }
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {isUpdating ? "Updating..." : "Update Status"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkActions;
